import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import {Box, Paper, Typography} from '@mui/material'
import type {FC, ReactElement} from 'react'
import {useCustomTranslation} from '@hooks/useCustomTranslation'

type Props = {
  /** Human-readable name of the product area (e.g. "Alpinist", "Edge"). */
  segment: string
}

/**
 * Centered "you can't access this area" view shown at a segment's content entry
 * point when the user lacks read access. Entry points (nav items, tabs, panels)
 * stay visible; opening them renders this message instead of the content.
 */
const MissingPermissions: FC<Props> = ({segment}): ReactElement => {
  const [t] = useCustomTranslation('common')

  return (
    <Box
      data-testid="mbai-missing-permissions"
      sx={{
        display: 'flex',
        flex: 1,
        width: '100%',
        height: '100%',
        minHeight: 240,
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          maxWidth: 420,
          textAlign: 'center',
          p: 4,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          backgroundColor: 'background.fadedOpaque',
        }}
      >
        <LockOutlinedIcon sx={{fontSize: 40, color: 'text.secondary'}} />
        <Typography variant="body1" color="text.secondary">
          {t('access_denied.missing_permissions', {segment})}
        </Typography>
      </Paper>
    </Box>
  )
}

export default MissingPermissions
